import {
  FormControl,
  FormHelperText,
  FormLabel,
  InputProps,
  Skeleton,
} from '@mui/joy'
import { isValid } from 'date-fns'
import { ru, enUS, fr } from 'date-fns/locale'
import { ParseKeys } from 'i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import type { InputMaskElement } from 'imask'
import { ChangeEvent, forwardRef, useMemo } from 'react'
import DatePicker from 'react-datepicker'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IMaskInput } from 'react-imask'
import { uiDateFormat } from '~/shared/config/datetime'
import { formatDateForApi } from '~/shared/lib/date'
import { useParserI18nMessage } from '~/shared/lib/useParserI18nMessage'
import { CalendarIcon } from '~/shared/ui/Icons'
import { DatePickerPopper, Input } from '../ui/styled'
import 'react-datepicker/dist/react-datepicker.css'

export type DatePickerInputProps = {
  name: string
  skeletonShow?: boolean
  label?: ParseKeys
  placeholder?: ParseKeys
} & Omit<InputProps, 'name' | 'slotProps' | 'placeholder'>

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
}

const MaskAdapter = forwardRef<HTMLElement, CustomProps>(function Wrapper(
  { onChange, ...other },
  ref: InputMaskElement,
) {
  return (
    <IMaskInput
      {...other}
      mask='##.##.####'
      definitions={{
        '#': /[0-9]/,
      }}
      onInput={(value: ChangeEvent<HTMLInputElement>) => onChange(value)}
      inputRef={ref}
      autoComplete='off'
    />
  )
})

export function DatePickerInput({
  name,
  skeletonShow,
  label,
  placeholder,
  ...inputProps
}: DatePickerInputProps) {
  const { i18n, t } = useTranslation()
  const { parserI18nMessage } = useParserI18nMessage()

  const currentDateFnLocal = useMemo(() => {
    const language = i18n.language
    if (language === 'ru') return ru
    if (language === 'fr') return fr
    return enUS
  }, [i18n.language])

  const {
    setValue,
    formState: { errors },
    watch,
    trigger,
  } = useFormContext()
  const selected = watch(name) || null

  const handleDateChange = (date: Date | null) => {
    setValue(name, isValid(date) ? formatDateForApi(date as Date) : '')
  }

  const messageError = errors[name]?.message as string
  const isValidateError = Boolean(errors[name])

  watch((_, { name: watchName }) => {
    if (watchName === name && isValidateError) {
      trigger(name)
    }
  })

  return (
    <FormControl
      error={isValidateError}
      sx={{
        position: 'initial',
      }}
    >
      {label && <FormLabel>{t(label)}</FormLabel>}
      <DatePicker
        selected={selected}
        onChange={handleDateChange}
        dateFormat={uiDateFormat}
        locale={currentDateFnLocal}
        {...(placeholder ? { placeholderText: t(placeholder) } : {})}
        popperContainer={DatePickerPopper}
        disabledKeyboardNavigation
        readOnly={inputProps.readOnly}
        customInput={
          <Input
            {...inputProps}
            startDecorator={<CalendarIcon />}
            name={name}
            fullWidth
            error={isValidateError}
            size='lg'
            slotProps={{
              ...(skeletonShow ? { root: { component: Skeleton } } : {}),
              input: {
                component: MaskAdapter,
                name,
              },
            }}
          />
        }
      />
      {isValidateError && (
        <FormHelperText data-testid={`error-message-${label && t(label)}`}>
          {parserI18nMessage(messageError)}
        </FormHelperText>
      )}
    </FormControl>
  )
}
